import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Box } from '@mui/material'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, Typography } from '@carto/meridian-ds/components'
import { EditOutlined } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from 'app/state'
import { showGlobalBuilderError } from 'features/builder/state/builderSlice'
import useTimer from 'features/common/hooks/useTimer'
import { TakeOverResult, TAKEOVER_TIMEOUT_SEC } from 'features/builder/types'
import { useTakeOverMutation } from 'features/builder/hooks/collaboration'

export type BuilderTakeOverRequestDialogProps = {
  open: boolean
  mode: 'forced' | 'userRequest'
  onFinished: (result: TakeOverResult) => void
}

function BuilderTakeOverRequestDialog({ open, mode, onFinished }: BuilderTakeOverRequestDialogProps) {
  const dispatch = useAppDispatch()

  const currentMapLockStatus = useAppSelector((state) => state.builder.currentMapLockStatus)

  const { mutateAsync: requestTakeOverAndLock, cancel: cancelTakeover, isLoading: isTakingOver } = useTakeOverMutation()

  const [takeOverAutoAcceptDeadline, setTakeOverAutoAcceptDeadline] = useState<number | undefined>()
  const timeLeft = useTimer(takeOverAutoAcceptDeadline)

  const user = currentMapLockStatus?.email

  const handlePreviewOnly = () => {
    onFinished('cancel')
  }

  const handleCancel = () => {
    cancelTakeover()
    onFinished('cancel')
  }

  const handleClose = () => {
    if (mode !== 'forced') {
      cancelTakeover()
      onFinished('cancel')
    }
  }

  const handleTakeOverClick = async () => {
    setTakeOverAutoAcceptDeadline(Date.now() + TAKEOVER_TIMEOUT_SEC * 1000)
    try {
      onFinished(await requestTakeOverAndLock())
    } catch (error) {
      dispatch(showGlobalBuilderError(String(error)))
      onFinished('cancel')
    } finally {
      setTakeOverAutoAcceptDeadline(undefined)
    }
  }

  const showPreviewOnlyButton = mode === 'forced' && !isTakingOver
  const showCancelButton = !showPreviewOnlyButton

  return (
    <Dialog open={open} onClose={mode !== 'forced' ? handleClose : undefined}>
      <DialogHeader
        title={
          <FormattedMessage
            id={
              mode === 'forced'
                ? 'builder.takeOverRequestDialog.titleForced'
                : 'builder.takeOverRequestDialog.titleVoluntary'
            }
          />
        }
        onClose={mode !== 'forced' ? handleClose : undefined}
      />
      <DialogContent>
        <Typography component="span" variant="body2" color="textSecondary">
          <FormattedMessage
            id="builder.takeOverRequestDialog.message1"
            values={{
              user: user,
              strong: (content) => <strong>{content}</strong>
            }}
          />
        </Typography>

        {typeof timeLeft === 'number' && (
          <Box mt={2}>
            <Typography component="span" variant="body2" color="textSecondary">
              <FormattedMessage
                id="builder.takeOverRequestDialog.message2"
                values={{
                  timer: Math.ceil(timeLeft / 1000),
                  strong: (content) => <strong>{content}</strong>
                }}
              />
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogFooter
        secondaryActions={
          showPreviewOnlyButton && (
            <Button onClick={handlePreviewOnly} color="primary">
              <FormattedMessage id="builder.takeOverRequestDialog.viewOnly" />
            </Button>
          )
        }
      >
        {showCancelButton && (
          <Button onClick={handleCancel} color="primary">
            <FormattedMessage id="common.button.cancel" />
          </Button>
        )}

        <Button
          onClick={handleTakeOverClick}
          variant="contained"
          color="primary"
          loading={isTakingOver}
          loadingPosition="start"
          startIcon={<EditOutlined />}
        >
          <FormattedMessage id="builder.takeOverRequestDialog.takeOverEditing" />
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default BuilderTakeOverRequestDialog
